/**
 * PackageEditorWorkspace
 *
 * Unified editor workspace for correspondence packages.
 * Shared between CorrespondencePackagePage and CorrespondenceModal.
 *
 * Uses RTKQ hooks directly for granular subscriptions.
 *
 * DESIGN NOTE: Radical "AAA" redesign.
 * - Canvas/Desk metaphor
 * - Floating elements with glassmorphism
 * - Refined spacing and typography
 *
 * Coverage mapping is now per-component. Each template component that requires
 * coverage data (1P, 3P, or Health) shows its own coverage selectors in the
 * expanded edit section.
 */

import { DocumentTextIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { useCorrespondencePackageRetrieveQuery } from '@/services/api/correspondenceService';

import { useCorrespondenceUI } from '../contexts/CorrespondenceUIContext';
import { useLienWorkflowConfig, usePackageComponents } from '../hooks';
import { PackagePreviewPane } from '../package-view/PackagePreviewPane';
import { LienFirmPreferencesBanner } from './LienFirmPreferencesBanner';
import { PackageComponentsList } from './PackageComponentsList';

const PANEL_COMPACT = 200;
const PANEL_EXPANDED = 360;
const COLLAPSE_DELAY_MS = 400;

interface PackageEditorWorkspaceProps {
  className?: string;
}

export const PackageEditorWorkspace: React.FC<PackageEditorWorkspaceProps> = ({
  className = '',
}) => {
  const { packageId, packageType, matterId } = useCorrespondenceUI();

  const { components } = usePackageComponents({
    packageId,
    skip: !packageId,
  });

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout>>();

  const isExpanded = isHovered || showAddPanel;

  const handleMouseEnter = useCallback(() => {
    clearTimeout(collapseTimer.current);
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    collapseTimer.current = setTimeout(
      () => setIsHovered(false),
      COLLAPSE_DELAY_MS,
    );
  }, []);

  // Fetch package data for mutability check
  const { data: packageData } = useCorrespondencePackageRetrieveQuery(
    { matterId, id: packageId || '' },
    { skip: !packageId },
  );

  // Fetch lien workflow config when viewing a lien notice package
  const isLienNotice = packageType === 'lien_notice';
  const { config: lienConfig, notes: lienNotes } = useLienWorkflowConfig({
    matterId,
    skip: !isLienNotice,
  });

  const showRenderedPdfFallback =
    (components?.length ?? 0) === 0 &&
    Boolean(packageData?.rendered_pdf_url) &&
    !packageData?.is_mutable;

  if (!packageId) return null;

  return (
    <div className={`relative flex h-full ${className} bg-transparent`}>
      {/* Floating document list panel */}
      <aside
        className={cn(
          'absolute top-0 left-0 h-full z-20 flex flex-col gap-2 transition-[width,box-shadow] duration-200 ease-in-out',
          isExpanded && 'shadow-xl',
        )}
        style={{ width: isExpanded ? PANEL_EXPANDED : PANEL_COMPACT }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex-1 bg-white rounded-xl border border-zinc-200 overflow-hidden flex flex-col min-h-0">
          <PackageComponentsList
            showAddPanel={showAddPanel}
            onShowAddPanel={setShowAddPanel}
          />
        </div>

        {isLienNotice && lienConfig && (
          <LienFirmPreferencesBanner config={lienConfig} notes={lienNotes} />
        )}
      </aside>

      {/* Preview canvas - offset by compact width so it's always visible */}
      <main
        className="flex-1 overflow-auto"
        style={{ marginLeft: PANEL_COMPACT }}
      >
        {components && components.length > 0 || showRenderedPdfFallback ? (
          <PackagePreviewPane />
        ) : (
          <div className="h-full flex items-center justify-center p-6">
            <div className="text-center max-w-xs p-8 rounded-xl border-2 border-dashed border-zinc-200 bg-white/60">
              <DocumentTextIcon className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-zinc-700 mb-1">
                Empty Canvas
              </h3>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Add documents from the left to start assembling your package.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
